import { memo, useMemo, useState } from 'react';
import { useMount } from 'react-use';
import type { SunlightTimes } from 'types/sunlightTimes';
import styles from './DayNightShading.module.scss';
import { useDayNightShadingBackground } from './useDayNightShadingBackground';

interface Props {
  sunlightTimes: SunlightTimes;
  /** Forecast day index into sunlightTimes / forecast arrays (drives gradient per day). */
  forecastDayIndex: number;
  /** Number of day columns in the visible strip (for flex width). */
  numDays: number;
  /** 0-based column index in the visible strip (for last-column flex adjustment). */
  columnIndex: number;
  isPaywalled: boolean;
  shadingOffset?: number;
}

const DayNightShading: React.FC<Props> = ({
  sunlightTimes,
  numDays = 1,
  forecastDayIndex,
  columnIndex,
  isPaywalled = false,
  shadingOffset = 0,
}) => {
  const [isMounted, setIsMounted] = useState(false);

  const { background } = useDayNightShadingBackground({
    sunlightTimes,
    dayIndex: forecastDayIndex,
    offset: shadingOffset,
  });

  const isLastChild = columnIndex === numDays - 1;

  // Only apply dynamic flex styles on client to prevent hydration mismatch
  const containerStyle = useMemo(
    () =>
      isMounted ? { flex: `0 0 calc(${100 / numDays}% - ${isLastChild ? 0 : 1}px)` } : undefined,
    [isMounted, isLastChild, numDays],
  );

  const shadingStyle = useMemo(
    () => ({
      background,
      height: '100%',
      width: '100%',
    }),
    [background],
  );

  useMount(() => {
    setIsMounted(true);
  });

  return (
    <div
      className={styles.dayNightShadingContainer}
      style={containerStyle}
      data-testid="day-night-shading"
    >
      <div style={!isPaywalled ? shadingStyle : undefined} />
    </div>
  );
};

export default memo(DayNightShading);
